"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Download, ShieldCheck, ShieldOff } from "lucide-react";
import { useTranslations } from "next-intl";
import { useWatch } from "react-hook-form";
import {
	Link,
	List,
	RadioCard,
	SimpleDialog,
	Stack,
	Text,
} from "@leabankab/component-library";
import { trackGa4Event } from "@/analytics/ga4";
import {
	CheckboxConsentField,
	ConfirmationButton,
	FormContainer,
} from "@/components/form";
import { BenefitList, Heading } from "@/components/ui";
import { useRuntimeConfig } from "@/config/runtime-config-context";
import {
	applyMockFormDefaults,
	getMockInsuranceDefaults,
} from "@/dev/console/data/mock-form-values";
import { useCurrency, useSubmitTask } from "@/hooks";
import { AMOUNT_PLACEHOLDER } from "@/shared/amount";
import { TaskType } from "@/types";
import useInsuranceForm, {
	type InsuranceForm,
} from "../hooks/use-insurance-form";
import { InsuranceSalePayload, LoanInsuranceSaleContext } from "../types";
import { getInsuranceCost } from "../utils/getInsuranceCost";

interface Props {
	context: LoanInsuranceSaleContext;
	taskId: string;
}

const mapInsuranceFormToPayload = (
	values: InsuranceForm
): InsuranceSalePayload => {
	const hasAcceptedTerms = Boolean(values.insurance && values.termsConsent);
	const consentText = hasAcceptedTerms ? "Accepted in app" : "";

	return {
		Insurance: values.insurance ?? false,
		ThirdpartyInsuranceConsent: hasAcceptedTerms,
		CorrectInfoConfirmation: hasAcceptedTerms,
		ThirdpartyInsuranceConsentText: consentText,
		CorrectInfoConfirmationText: consentText,
	};
};

const LoanInsuranceSalePage = ({ context, taskId }: Props) => {
	const t = useTranslations("pages.InsuranceSalePage");
	const { format } = useCurrency();
	const { mock } = useRuntimeConfig();
	const methods = useInsuranceForm().useForm;
	const { control, handleSubmit, setValue, reset } = methods;
	const wantsInsurance = useWatch({ control, name: "insurance" });
	const [termsDialogOpen, setTermsDialogOpen] = useState(false);
	const isSubmittingRef = useRef(false);
	const hasTrackedOfferView = useRef(false);

	useEffect(() => {
		if (hasTrackedOfferView.current) return;
		hasTrackedOfferView.current = true;
		trackGa4Event("insurance_offer_viewed", {
			product_type: "loan",
		});
	}, []);

	useEffect(() => {
		reset(
			applyMockFormDefaults(
				{ insurance: undefined, termsConsent: false },
				getMockInsuranceDefaults(),
				mock
			)
		);
	}, [reset, mock]);

	const submit = useSubmitTask<InsuranceSalePayload>({
		taskId,
		processType: context.process,
		taskType: TaskType.InsuranceSale,
	});

	const submitForm = useCallback(
		() =>
			handleSubmit(async (values) => {
				if (isSubmittingRef.current) return;
				isSubmittingRef.current = true;
				await submit(mapInsuranceFormToPayload(values), () => {
					if (!values.insurance || !values.termsConsent) return;
					trackGa4Event("insurance_added", {
						product_type: "loan",
					});
					trackGa4Event("signing_terms_accepted", {
						product_type: "loan",
						terms_type: "insurance_offer",
					});
				});
			})(),
		[handleSubmit, submit]
	);

	const closeTermsDialog = useCallback(() => {
		setTermsDialogOpen(false);
		setValue("insurance", undefined, { shouldValidate: true });
		setValue("termsConsent", false, { shouldValidate: true });
	}, [setValue]);

	const insuranceCost = getInsuranceCost(context.offer);
	const formattedInsuranceCost =
		insuranceCost === null ? null : format(insuranceCost);
	const insurancePrice =
		formattedInsuranceCost === null
			? AMOUNT_PLACEHOLDER
			: t("priceValue", { price: formattedInsuranceCost });

	const insuranceOptions = [
		{
			value: true,
			icon: <ShieldCheck aria-hidden="true" />,
			label: t("insuranceChoice.withInsurance.label"),
			description:
				formattedInsuranceCost === null
					? t("insuranceChoice.withInsurance.descriptionWithoutPrice")
					: t("insuranceChoice.withInsurance.description", {
							price: formattedInsuranceCost,
						}),
		},
		{
			value: false,
			icon: <ShieldOff aria-hidden="true" />,
			label: t("insuranceChoice.withoutInsurance.label"),
			description: t("insuranceChoice.withoutInsurance.description"),
		},
	];

	const coverageItems = [
		{
			title: t("coverageList.disease.title"),
			description: t("coverageList.disease.description"),
		},
		{
			title: t("coverageList.unemployment.title"),
			description: t("coverageList.unemployment.description"),
		},
		{
			title: t("coverageList.death.title"),
			description: t("coverageList.death.description"),
		},
	];

	const coveredEvents = ["disease", "unemployment", "death"] as const;
	const declarations = [
		"residency",
		"employment",
		"jobSecurity",
		"healthCondition",
	] as const;

	return (
		<FormContainer
			methods={methods}
			getPayloadPreview={mapInsuranceFormToPayload}
		>
			<Text>{t("description")}</Text>

			<BenefitList items={coverageItems.map((item) => item.title)} />

			<Stack>
				<Text>{t("price")}</Text>
				<Text fontWeight="medium" textStyle="body-lg">
					{insurancePrice}
				</Text>
			</Stack>

			<RadioCard.Group
				label={t("insuranceChoice.label")}
				value={wantsInsurance ?? null}
				onValueChange={(selectedInsurance: boolean) => {
					setValue("insurance", selectedInsurance, {
						shouldTouch: true,
						shouldValidate: true,
					});
					if (!selectedInsurance) {
						setValue("termsConsent", false, { shouldValidate: true });
					}
					if (selectedInsurance) {
						trackGa4Event("insurance_offer_clicked", {
							product_type: "loan",
						});
						setTermsDialogOpen(true);
					}
				}}
			>
				{insuranceOptions.map((option) => (
					<RadioCard.Root key={String(option.value)} value={option.value}>
						<RadioCard.Indicator />
						{option.icon}
						<RadioCard.Content>
							<RadioCard.Label>{option.label}</RadioCard.Label>
							<RadioCard.Description>
								{option.description}
							</RadioCard.Description>
						</RadioCard.Content>
					</RadioCard.Root>
				))}
			</RadioCard.Group>

			{wantsInsurance === false && (
				<ConfirmationButton
					label={t("confirmationButton.continue")}
					onSubmit={submitForm}
				/>
			)}

			<SimpleDialog
				open={termsDialogOpen}
				onOpenChange={(open) => {
					if (!open) closeTermsDialog();
				}}
				title={t("termsDialog.title")}
				closeLabel={t("termsDialog.close")}
			>
				<Stack gap={3}>
					<Text>{t("termsDialog.intro")}</Text>

					<Heading level={2} textStyle="heading-xs">
						{t("termsDialog.coverageIntro")}
					</Heading>

					<List.Root gap={1}>
						{coveredEvents.map((item) => (
							<List.Item key={item}>
								{t(`termsDialog.coveredEvents.${item}`)}
							</List.Item>
						))}
					</List.Root>

					<Link
						href={t("termsConsent.downloadLink")}
						target="_blank"
						className="flex items-center gap-1"
					>
						<Download aria-hidden="true" size={16} />{" "}
						{t("termsConsent.downloadText")}
					</Link>

					<Stack gap={1}>
						<Heading level={2} textStyle="heading-xs">
							{t("termsDialog.confirmationIntro")}
						</Heading>
						<List.Root gap={1}>
							{declarations.map((item) => (
								<List.Item key={item}>
									{t(`termsDialog.declarations.${item}`)}
								</List.Item>
							))}
						</List.Root>
					</Stack>

					<CheckboxConsentField
						name="termsConsent"
						label={t("termsConsent.text")}
					/>
					<ConfirmationButton
						label={t("termsDialog.addButton")}
						onSubmit={submitForm}
					/>
				</Stack>
			</SimpleDialog>
		</FormContainer>
	);
};

export default LoanInsuranceSalePage;
